import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';

interface Member {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  membershipStatus: 'Active' | 'Trial' | 'Expired';
  subscriptionPlan?: string;
  joinedDate: string;
  profileImage?: string;
  createdAt?: string;
}

const MembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch members
  const { data: membersData = [], isLoading } = useQuery({
    queryKey: ['members'],
    queryFn: () => userService.getAllMembers(),
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['memberStats'],
    queryFn: () => userService.getMemberStats(),
  });

  // Transform members
  const members: Member[] = Array.isArray(membersData)
    ? membersData.map((member: any) => ({
        _id: member._id,
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        membershipStatus: member.membershipStatus || 'Trial',
        subscriptionPlan: member.subscriptionPlan || 'Trial',
        joinedDate: member.createdAt ? new Date(member.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Unknown',
        profileImage: member.profilePhotoUrl,
        createdAt: member.createdAt,
      }))
    : [];

  const filteredMembers = members.filter(member =>
    member.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredMembers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 4;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 1) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 1; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  const statCards = [
    { label: 'Total Members', value: stats?.totalMembers || '0', change: '-12%', icon: '👥' },
    { label: 'Active', value: stats?.activeMembers || '0', change: '+5%', icon: '✓' },
    { label: 'New this Month', value: stats?.newThisMonth || '0', change: '-18%', icon: '👤' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500 bg-opacity-20 text-green-400';
      case 'Trial':
        return 'bg-blue-500 bg-opacity-20 text-blue-400';
      case 'Expired':
        return 'bg-red-500 bg-opacity-20 text-red-400';
      default:
        return 'bg-gray-500 bg-opacity-20 text-gray-400';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading members...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Member Directory</h1>
          <p className="text-gray-400">Manage and monitor your fitness community members across all tiers</p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search members by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-[#23F47C]"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <span className="text-gray-400 text-sm">{stat.label}</span>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-white">{stat.value}</span>
                {stat.change && <span className="text-red-400 text-sm">{stat.change}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Members Table */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Members List</h2>
            <div className="flex gap-3">
              <button className="px-4 py-2 border border-gray-700 text-gray-300 rounded hover:bg-gray-800 transition-all text-sm font-medium">
                Export CSV
              </button>
              <button className="px-4 py-2 border border-gray-700 text-gray-300 rounded hover:bg-gray-800 transition-all text-sm font-medium">
                Filters
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Member</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Email Address</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Subscription Plan</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Membership Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Joined Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.map((member) => (
                  <tr key={member._id} className="border-b border-gray-800 hover:bg-gray-800 bg-opacity-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={member.firstName}
                              className="w-10 h-10 rounded-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="text-gray-300 text-sm font-medium">
                              {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                            </span>
                          )}
                        </div>
                        <span className="text-white font-medium">{member.firstName} {member.lastName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{member.email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300 font-medium">{member.subscriptionPlan}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(member.membershipStatus)}`}>
                        {member.membershipStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{member.joinedDate}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => navigate(`/members/${member._id}`)}
                          className="p-2 text-gray-400 hover:text-[#23F47C] transition-all"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
            <div className="text-gray-400 text-sm">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} members
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    currentPage === page
                      ? 'bg-[#23F47C] text-gray-900'
                      : 'border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C]'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-800 text-gray-400 hover:border-[#23F47C] hover:text-[#23F47C] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembersPage;
